import { Controller } from "@hotwired/stimulus"

// TREND Files upload (plan F2). Small files POST whole; files over the 64MB
// threshold stream in 8MB upload-session chunks (start/append/finish) so no
// single dyno-to-Dropbox call outlives Heroku's router window (H12).
// Duplicate names come back as 409 + prompt copy (spec 7.4); the user picks
// a new name and the upload retries under it.
//
// Onboarding-upload committee extension, all OPT-IN so the Files page keeps
// its historical behavior untouched:
//   reload value (default true)  - false renders per-file result rows into
//     the list target instead of reloading (a reload inside the onboarding
//     wizard would be disorienting; inside any form it would be destructive)
//   maxFileSize value (default 0 = off) - client-side pre-check so nobody
//     watches a doomed progress bar for a file the server will reject
//   in-flight guard - while uploads are running, navigating away (Turbo
//     visit or full unload) asks first; finished files are already saved
const CHUNK_THRESHOLD = 64 * 1024 * 1024
const CHUNK_SIZE = 8 * 1024 * 1024

export default class extends Controller {
  static targets = ["input", "uploadModal", "folderModal", "dropZone", "list",
                    "progress", "progressBar", "progressLabel", "progressPercent"]
  static values = {
    url: String, folder: Number,
    reload: { type: Boolean, default: true },
    maxFileSize: { type: Number, default: 0 }
  }

  connect() {
    this.inFlight = 0
    this.onBeforeUnload = (e) => {
      if (this.inFlight > 0) { e.preventDefault(); e.returnValue = "" }
    }
    this.onBeforeVisit = (e) => {
      if (this.inFlight > 0 &&
          !confirm("Files are still uploading. Leave anyway? Finished files are already saved.")) {
        e.preventDefault()
      }
    }
    window.addEventListener("beforeunload", this.onBeforeUnload)
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
  }

  openUpload() { this.uploadModalTarget.classList.remove("hidden") }
  openNewFolder() { this.folderModalTarget.classList.remove("hidden") }

  closeModals() {
    if (this.hasUploadModalTarget) this.uploadModalTarget.classList.add("hidden")
    if (this.hasFolderModalTarget) this.folderModalTarget.classList.add("hidden")
  }

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("ring-2", "ring-[#C4A484]")
  }

  dragLeave() {
    this.dropZoneTarget.classList.remove("ring-2", "ring-[#C4A484]")
  }

  drop(event) {
    event.preventDefault()
    this.dragLeave()
    if (!this.folderValue) return
    this.uploadAll(Array.from(event.dataTransfer.files))
  }

  submit(event) {
    event.preventDefault()
    this.closeModals()
    this.uploadAll(Array.from(this.inputTarget.files))
    if (this.hasInputTarget) this.inputTarget.value = ""
  }

  async uploadAll(files) {
    if (!files.length) return
    if (!this.reloadValue) return this.uploadAllInline(files)
    this.showProgress()
    try {
      for (let i = 0; i < files.length; i++) {
        this.setProgressLabel(`Uploading ${files[i].name} (${i + 1}/${files.length})`)
        await this.uploadOne(files[i], files[i].name)
      }
      window.location.reload()
    } catch (error) {
      this.hideProgress()
      alert(error.message || "Upload failed. Please try again.")
    }
  }

  // The no-reload mode: every file gets its own result row, one failure
  // never aborts the queue, and the page (with any surrounding form state)
  // is left exactly as it was.
  async uploadAllInline(files) {
    this.inFlight += files.length
    this.showProgress()
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const row = this.addRow(file.name)
      try {
        if (this.maxFileSizeValue && file.size > this.maxFileSizeValue) {
          const rejection = new Error("This file is over our 4GB upload limit. Mention it to your strategist and we will grab it from you directly.")
          rejection.noRetry = true // retrying an over-cap file is a guaranteed failure
          throw rejection
        }
        this.setProgressLabel(`Uploading ${file.name} (${i + 1}/${files.length})`)
        await this.uploadOne(file, file.name)
        this.finishRow(row, file.name, true)
      } catch (error) {
        this.finishRow(row, file.name, false, error.message, error.noRetry ? null : file)
      } finally {
        this.inFlight -= 1
      }
    }
    this.hideProgress()
    this.dispatch("uploaded")
  }

  addRow(name) {
    const row = document.createElement("div")
    row.className = "flex items-center justify-between text-sm bg-[#F5F3F0] rounded-lg px-3 py-2"
    row.innerHTML = `<span class="truncate text-[#2D2D2D]"></span><span class="text-xs text-[#8C8C8C] ml-3 shrink-0">Uploading...</span>`
    row.firstChild.textContent = name
    this.listTarget.appendChild(row)
    return row
  }

  finishRow(row, name, ok, message, file) {
    const status = row.lastChild
    if (ok) {
      status.textContent = "Sent"
      status.className = "text-xs text-[#9CAF88] ml-3 shrink-0 font-medium"
    } else {
      status.textContent = ""
      const label = document.createElement("span")
      label.className = "text-xs text-[#E57373]"
      label.textContent = message || "Upload failed."
      status.className = "ml-3 shrink-0 flex items-center"
      status.append(label)
      if (file) { // no file = a permanent rejection (over cap); retry would just fail again
        const retry = document.createElement("button")
        retry.type = "button"
        retry.className = "text-xs text-[#C4A484] underline ml-2"
        retry.textContent = "Retry"
        retry.addEventListener("click", () => { row.remove(); this.uploadAllInline([file]) })
        status.append(retry)
      }
    }
  }

  async uploadOne(file, name) {
    const result = file.size > CHUNK_THRESHOLD
      ? await this.chunkedUpload(file, name)
      : await this.smallUpload(file, name)

    if (result.duplicate) {
      const newName = window.prompt(result.prompt, name)
      if (!newName || newName === name) throw new Error("Upload cancelled.")
      return this.uploadOne(file, newName)
    }
  }

  async smallUpload(file, name) {
    const data = new FormData()
    data.append("folder_id", this.folderValue)
    data.append("file", file, name)
    const response = await this.post(data)
    this.setProgressPercent(100)
    return response
  }

  async chunkedUpload(file, name) {
    // start (declared size lets the server fast-fail an over-cap file
    // before any bytes move; the real bound is enforced per chunk)
    const start = await this.post(this.chunkForm("start", {
      name: name, size: file.size, chunk: file.slice(0, CHUNK_SIZE)
    }))
    if (start.duplicate) return start

    // append middle chunks, finish on the last
    let offset = CHUNK_SIZE
    while (offset < file.size) {
      const end = Math.min(offset + CHUNK_SIZE, file.size)
      const phase = end >= file.size ? "finish" : "append"
      const response = await this.post(this.chunkForm(phase, {
        name: name, session_id: start.session_id, offset: offset, chunk: file.slice(offset, end)
      }))
      if (response.duplicate) return response
      offset = end
      this.setProgressPercent(Math.round((offset / file.size) * 100))
    }
    return {}
  }

  chunkForm(phase, fields) {
    const data = new FormData()
    data.append("phase", phase)
    data.append("folder_id", this.folderValue)
    for (const [key, value] of Object.entries(fields)) {
      if (value instanceof Blob) data.append(key, value, "chunk")
      else data.append(key, value)
    }
    return data
  }

  async post(formData) {
    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Accept": "application/json"
      },
      body: formData
    })
    const body = await response.json().catch(() => ({}))
    if (response.status === 409 && body.error === "duplicate") {
      return { duplicate: true, prompt: body.prompt }
    }
    // Prefer the server's human copy (prompt) over the machine code (error):
    // the inline rows and the alert both show this message to clients.
    if (!response.ok) throw new Error(body.prompt || body.error || `Upload failed (${response.status}).`)
    return body
  }

  showProgress() {
    this.progressTarget.classList.remove("hidden")
    this.setProgressPercent(0)
  }

  hideProgress() { this.progressTarget.classList.add("hidden") }
  setProgressLabel(text) { this.progressLabelTarget.textContent = text }

  setProgressPercent(percent) {
    this.progressPercentTarget.textContent = `${percent}%`
    this.progressBarTarget.style.width = `${percent}%`
  }
}
